import styled, { css } from 'styled-components';
import { lineHeight, theme, toRem, TYPE_ROLES, TypeRole } from 'design';

/**
 * Phase 1 (type scale).
 * On-scale sizes are routed through `theme.font.size.*` (Figma-derived tokens).
 * The heading sizes (H1 22/27, H2 19.25/22) stay raw `toRem(n)` — they're
 * intentionally NOT collapsed in Phase 1 and are deferred to the future type
 * re-scale (the ambiguous 22px → token mapping needs design). Tagged
 * `TODO(type-rescale)`; see the DESIGN_ALIGNMENT_PLAN decision log.
 * Grep `TODO(type-rescale)` to find them.
 */

interface IWrapProps {
  allowWordWrap?: boolean;
}

const allowWordWrapStyles = css<IWrapProps>`
  ${({ allowWordWrap }) =>
    allowWordWrap &&
    css`
      word-break: break-word; // Fallback for older browsers and IE
      overflow-wrap: anywhere;
      white-space: normal;
    `}
`;

// Headlines
interface IHeadlineProps extends IWrapProps {
  color?: string;
  fontWeight?: 'regular' | 'medium' | 'semibold' | 'bold';
  marginBottom?: number;
}

export const H1 = styled.h1<IHeadlineProps>`
  margin-bottom: ${toRem(theme.space.md)};
  font-family: ${theme.font.family.averta};
  font-weight: ${theme.font.weight.bold};
  /* TODO(type-rescale): off-scale heading — deferred to the future type re-scale (not collapsed in Phase 1) */
  font-size: ${toRem(22)};
  /* TODO(type-rescale): paired with the deferred heading size above */
  line-height: 100%;
  color: ${theme.color.neutral[900]};

  @media (min-width: ${theme.breakpoint.med}px) {
    /* TODO(type-rescale): off-scale heading — deferred to the future type re-scale (not collapsed in Phase 1) */
    font-size: ${toRem(27)};
  }

  ${({ marginBottom }) =>
    marginBottom &&
    css`
      margin-bottom: ${toRem(marginBottom)};
    `}

  ${({ fontWeight }) =>
    fontWeight === 'regular' &&
    css`
      font-weight: ${theme.font.weight.regular};
    `}

${({ fontWeight }) =>
    fontWeight === 'medium' &&
    css`
      font-weight: ${theme.font.weight.medium};
    `}

${({ fontWeight }) =>
    fontWeight === 'semibold' &&
    css`
      font-weight: ${theme.font.weight.semiBold};
    `}

${({ fontWeight }) =>
    fontWeight === 'bold' &&
    css`
      font-weight: ${theme.font.weight.bold};
    `}

  ${({ color }) =>
    color &&
    css`
      color: ${color};
    `}

  ${allowWordWrapStyles}
`;

export const H2 = styled.h2<IHeadlineProps>`
  margin-bottom: 0;
  font-family: ${theme.font.family.euclid};
  font-weight: ${theme.font.weight.regular};
  /* TODO(type-rescale): off-scale heading — deferred to the future type re-scale (not collapsed in Phase 1) */
  font-size: ${toRem(19.25)};
  /* TODO(type-rescale): paired with the deferred heading size above */
  line-height: 119.2%;
  color: ${theme.color.neutral[900]};

  @media (min-width: ${theme.breakpoint.med}px) {
    /* TODO(type-rescale): off-scale heading — deferred to the future type re-scale (not collapsed in Phase 1) */
    font-size: ${toRem(22)};
  }

  ${({ marginBottom }) =>
    marginBottom &&
    css`
      margin-bottom: ${toRem(marginBottom)};
    `}

  ${({ fontWeight }) =>
    fontWeight === 'regular' &&
    css`
      font-weight: ${theme.font.weight.regular};
    `}

${({ fontWeight }) =>
    fontWeight === 'medium' &&
    css`
      font-weight: ${theme.font.weight.medium};
    `}

${({ fontWeight }) =>
    fontWeight === 'semibold' &&
    css`
      font-weight: ${theme.font.weight.semiBold};
    `}

${({ fontWeight }) =>
    fontWeight === 'bold' &&
    css`
      font-weight: ${theme.font.weight.bold};
    `}

  ${({ color }) =>
    color &&
    css`
      color: ${color};
    `}

  ${allowWordWrapStyles}
`;

export const H3 = styled.h3<IHeadlineProps>`
  margin-bottom: 0;
  font-family: ${theme.font.family.euclid};
  font-weight: ${theme.font.weight.semiBold};
  font-size: ${toRem(theme.font.size.sm)};
  line-height: ${lineHeight(theme.font.size.sm)};
  color: ${theme.color.neutral[900]};

  @media (min-width: ${theme.breakpoint.med}px) {
    font-size: ${toRem(theme.font.size.lg)};
    line-height: ${lineHeight(theme.font.size.lg)};
  }

  ${({ marginBottom }) =>
    marginBottom &&
    css`
      margin-bottom: ${toRem(marginBottom)};
    `}

  ${({ fontWeight }) =>
    fontWeight === 'regular' &&
    css`
      font-weight: ${theme.font.weight.regular};
    `}

${({ fontWeight }) =>
    fontWeight === 'medium' &&
    css`
      font-weight: ${theme.font.weight.medium};
    `}

${({ fontWeight }) =>
    fontWeight === 'semibold' &&
    css`
      font-weight: ${theme.font.weight.semiBold};
    `}

${({ fontWeight }) =>
    fontWeight === 'bold' &&
    css`
      font-weight: ${theme.font.weight.bold};
    `}

  ${({ color }) =>
    color &&
    css`
      color: ${color};
    `}

  ${allowWordWrapStyles}
`;

export const H4 = styled.h4<IHeadlineProps>`
  margin-bottom: 0;
  font-family: ${theme.font.family.euclid};
  font-weight: ${theme.font.weight.regular};
  font-size: ${toRem(theme.font.size.sm)};
  line-height: ${lineHeight(theme.font.size.sm)};
  color: ${theme.color.neutral[900]};

  ${({ marginBottom }) =>
    marginBottom &&
    css`
      margin-bottom: ${toRem(marginBottom)};
    `}

  ${({ fontWeight }) =>
    fontWeight === 'regular' &&
    css`
      font-weight: ${theme.font.weight.regular};
    `}

${({ fontWeight }) =>
    fontWeight === 'medium' &&
    css`
      font-weight: ${theme.font.weight.medium};
    `}

${({ fontWeight }) =>
    fontWeight === 'semibold' &&
    css`
      font-weight: ${theme.font.weight.semiBold};
    `}

${({ fontWeight }) =>
    fontWeight === 'bold' &&
    css`
      font-weight: ${theme.font.weight.bold};
    `}

  ${({ color }) =>
    color &&
    css`
      color: ${color};
    `}

  ${allowWordWrapStyles}
`;

// HR
interface IHorizontalRuleProps {
  color?: string;
  width?: number;
}

export const HR = styled.hr<IHorizontalRuleProps>`
  border: 0;
  border-top: ${toRem(2)} solid ${theme.color.neutral[200]};
  border-bottom: ${toRem(2)} solid transparent;
  width: 100%;

  ${({ color }) =>
    color &&
    css`
      color: ${color};
    `}

  ${({ width }) =>
    width &&
    css`
      width: ${width}%;
    `}
`;

// Text
interface ITextProps extends IWrapProps {
  fontWeight?: 'regular' | 'medium' | 'semibold' | 'bold';
  size?: 'default' | 'small' | 'xsmall' | 'xxsmall';
  capitalize?: boolean;
}

export const Text = styled.p<ITextProps>`
  font-family: ${theme.font.family.euclid};
  font-weight: ${theme.font.weight.regular};
  font-size: ${toRem(theme.font.size.sm)};
  line-height: ${lineHeight(theme.font.size.sm)};
  color: ${theme.color.neutral[900]};

  @media (min-width: ${theme.breakpoint.med}px) {
    font-size: ${toRem(theme.font.size.lg)};
    line-height: ${lineHeight(theme.font.size.lg)};
  }

  ${({ capitalize }) =>
    capitalize &&
    css`
      text-transform: capitalize;
    `}

  ${({ size }) =>
    size === 'small' &&
    css`
      font-size: ${toRem(theme.font.size.sm)};
      line-height: ${lineHeight(theme.font.size.sm)};

      @media (min-width: ${theme.breakpoint.med}px) {
        font-size: ${toRem(theme.font.size.sm)};
        line-height: ${lineHeight(theme.font.size.sm)};
      }
    `}

  ${({ size }) =>
    size === 'xsmall' &&
    css`
      font-size: ${toRem(theme.font.size.xs)};
      line-height: ${lineHeight(theme.font.size.xs)};

      @media (min-width: ${theme.breakpoint.med}px) {
        font-size: ${toRem(theme.font.size.xs)};
        line-height: ${lineHeight(theme.font.size.xs)};
      }
    `}

  ${({ size }) =>
    size === 'xxsmall' &&
    css`
      font-size: ${toRem(theme.font.size.xxs)};
      line-height: ${lineHeight(theme.font.size.xxs)};

      @media (min-width: ${theme.breakpoint.med}px) {
        font-size: ${toRem(theme.font.size.xxs)};
        line-height: ${lineHeight(theme.font.size.xxs)};
      }
    `}

  ${({ fontWeight }) =>
    fontWeight === 'regular' &&
    css`
      font-weight: ${theme.font.weight.regular};
    `}

  ${({ fontWeight }) =>
    fontWeight === 'medium' &&
    css`
      font-weight: ${theme.font.weight.medium};
    `}

  ${({ fontWeight }) =>
    fontWeight === 'semibold' &&
    css`
      font-weight: ${theme.font.weight.semiBold};
    `}

  ${({ fontWeight }) =>
    fontWeight === 'bold' &&
    css`
      font-weight: ${theme.font.weight.bold};
    `}

  ${allowWordWrapStyles}

  a {
    text-decoration: underline;
    cursor: pointer;
  }
`;

// Forms and Fields
export const Label = styled.label<IWrapProps>`
  margin: 0;
  font-family: ${theme.font.family.euclid};
  font-weight: ${theme.font.weight.medium};
  font-size: ${toRem(theme.font.size.sm)};
  line-height: ${lineHeight(theme.font.size.sm)};
  em {
    font-style: italic;
  }
  .optional {
    color: ${theme.color.neutral[300]};
  }

  ${allowWordWrapStyles}
`;

interface IMessageProps {
  hasError?: boolean;
  indent?: boolean;
  key?: string;
}

export const ValidationMessage = styled(Text).attrs({ size: 'xsmall' })<IMessageProps>`
  ${({ hasError }) =>
    hasError &&
    css`
      color: ${theme.color.danger[50]};
      text-align: center;
    `}

  ${({ indent }) =>
    indent &&
    css`
      padding-left: ${toRem(theme.space['3xl'])};
    `}
`;

export const FormError = styled(Text).attrs({
  size: 'small',
})`
  &:first-letter {
    text-transform: capitalize;
  }
  margin-bottom: ${toRem(theme.space['2xl'])};
  color: ${theme.color.danger[50]};
  text-align: center;
  overflow: hidden;
  text-overflow: ellipsis;
  max-width: 300px;
`;

export const ErrorText = styled(Text).attrs({
  size: 'small',
})`
  &:first-letter {
    text-transform: capitalize;
  }
  color: ${theme.color.danger[50]};
`;

// Type roles (Figma). Each role fixes its own size, line-height and weight.
export const typeRole = (role: TypeRole) => {
  const { size, weight, letterSpacing } = TYPE_ROLES[role];
  return css`
    font-family: ${theme.font.family.euclid};
    font-weight: ${theme.font.weight[weight]};
    font-size: ${toRem(theme.font.size[size])};
    line-height: ${lineHeight(theme.font.size[size])};
    ${letterSpacing &&
    css`
      letter-spacing: ${letterSpacing};
    `}
  `;
};

interface IRoleProps extends IWrapProps {
  color?: string;
}

const roleStyles = (role: TypeRole) => css<IRoleProps>`
  margin: 0;
  ${typeRole(role)}
  color: ${({ color }) => color || theme.color.neutral[900]};

  ${allowWordWrapStyles}
`;

export const H5 = styled.h5<IRoleProps>`
  ${roleStyles('h5')}
`;

export const H6 = styled.h6<IRoleProps>`
  ${roleStyles('h6')}
`;

export const BodyLg = styled.p<IRoleProps>`
  ${roleStyles('body-lg')}
`;

export const BodyMd = styled.p<IRoleProps>`
  ${roleStyles('body-md')}
`;

export const BodySm = styled.p<IRoleProps>`
  ${roleStyles('body-sm')}
`;

export const LabelLg = styled.span<IRoleProps>`
  ${roleStyles('label-lg')}
`;

export const LabelMd = styled.span<IRoleProps>`
  ${roleStyles('label-md')}
`;

export const LabelSm = styled.span<IRoleProps>`
  ${roleStyles('label-sm')}
`;

interface INavItemProps extends IRoleProps {
  fontWeight?: 'medium' | 'semibold';
}

export const NavItem = styled.span<INavItemProps>`
  ${({ fontWeight }) => roleStyles(fontWeight === 'semibold' ? 'nav-item-semibold' : 'nav-item')}
`;

export const Overline = styled.span<IRoleProps>`
  ${roleStyles('overline')}
`;

export const Caption = styled.span<IRoleProps>`
  ${roleStyles('caption')}
`;
